import axios from 'axios';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://private-salon-x-ecommerce.vercel.app/api';
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor to include token in requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await api.post('/auth/refresh-token', { refreshToken });
          const { token } = response.data;

          if (token) {
            localStorage.setItem('token', token);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Only remove authentication data if refresh token is invalid
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
      }
    }
    return Promise.reject(error);
  }
);

export const login = async (credentials) => {
  try {
    const response = await api.post('/auth/login', credentials);
    const { user, isAuthenticated } = response.data;

    if (response.status === 200 && user) {
      // For now, we'll use a dummy token since the server isn't providing one
      const dummyToken = 'dummy-token';
      localStorage.setItem('token', dummyToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
      toast.success('Login successful!');
      return { user, token: dummyToken, isAuthenticated };
    } else {
      console.error('Unexpected login response:', response);
      throw new Error('Invalid login response: Missing user data');
    }
  } catch (error) {
    console.error('Login error:', error);
    const message = error.response?.data?.message || error.message || 'Login failed. Please try again.';
    toast.error(message);
    throw error;
  }
};

export const signup = async (userData) => {
  try {
    const response = await api.post('/auth/signup', userData);
    const { user, isAuthenticated } = response.data;

    if (response.status === 201 && user) {
      // For now, we'll use a dummy token since the server isn't providing one
      const dummyToken = 'dummy-token';
      localStorage.setItem('token', dummyToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
      toast.success('Account created successfully!');
      return { user, token: dummyToken, isAuthenticated };
    } else {
      console.error('Unexpected signup response:', response);
      throw new Error('Invalid signup response: Missing user data');
    }
  } catch (error) {
    console.error('Signup error:', error);
    const message = error.response?.data?.message || error.message || 'Signup failed. Please try again.';
    toast.error(message);
    throw error;
  }
};

export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    toast.success('Logged out successfully');
  }
};

export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const isAuthenticated = JSON.parse(localStorage.getItem('isAuthenticated') || 'false');

    if (!token || !isAuthenticated) {
      return null;
    }

    const response = await api.get('/auth/me');
    const userData = response.data;

    if (userData) {
      if (storedUser !== JSON.stringify(userData)) {
        localStorage.setItem('user', JSON.stringify(userData));
      }
      return userData;
    }

    return null;
  } catch (error) {
    console.error('Error fetching current user:', error);
    // Don't remove isAuthenticated here
    return null;
  }
};

export const updateProfile = async (userData) => {
  try {
    const response = await api.put('/auth/update-profile', userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Profile update failed');
  }
};

export const changePassword = async (passwordData) => {
  try {
    const response = await api.post('/auth/change-password', passwordData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Password change failed');
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/password/forgot', { email });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Password reset request failed');
  }
};

export const resetPassword = async (resetData) => {
  try {
    const response = await api.post('/password/reset', resetData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Password reset failed');
  }
};

export const verifyEmail = async (token) => {
  try {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Email verification failed');
  }
};

export default api;

